import React from 'react';

export type TableSortDirection = 'asc' | 'desc';
export type DataTableMobileDisplay = 'title' | 'field' | 'actions' | 'hidden';

export interface DataTableColumn<T> {
    id: string;
    header: React.ReactNode;
    render: (row: T, index: number) => React.ReactNode;
    sortable?: boolean;
    sortKey?: string;
    sortValue?: (row: T) => string | number | null | undefined;
    headerClassName?: string;
    headerStyle?: React.CSSProperties;
    cellClassName?: string | ((row: T, index: number) => string | undefined);
    cellStyle?: React.CSSProperties | ((row: T, index: number) => React.CSSProperties | undefined);
    mobileLabel?: React.ReactNode;
    mobileDisplay?: DataTableMobileDisplay;
}

interface DataTableSortable {
    field: string;
    direction: TableSortDirection;
    onSort: (field: string) => void;
    clientSide?: boolean;
    getSortIcon?: (field: string, isActive: boolean, direction: TableSortDirection) => React.ReactNode;
}

interface DataTablePagination {
    currentPage: number;
    totalPages?: number;
    onPageChange: (page: number) => void;
    itemsPerPage?: number;
    totalItems?: number;
    itemsLabel?: string;
    mode?: 'full' | 'simple';
    info?: React.ReactNode;
    previousLabel?: React.ReactNode;
    nextLabel?: React.ReactNode;
    className?: string;
}

interface DataTableToolbarEntriesOption {
    value: number;
    label?: string;
}

interface DataTableToolbar {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    searchLabel?: React.ReactNode;
    searchClassName?: string;
    showSearchIcon?: boolean;
    entriesPerPage?: number;
    onEntriesPerPageChange?: (value: number) => void;
    entriesOptions?: DataTableToolbarEntriesOption[];
    showLabel?: React.ReactNode;
    entriesLabel?: React.ReactNode;
    className?: string;
    filteredCount?: number;
    totalCount?: number;
    countLabel?: string;
    onClearSearch?: () => void;
    clearSearchLabel?: React.ReactNode;
}

interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    rows: T[];
    rowKey: (row: T, index: number) => React.Key;
    tableId?: string;
    wrapperClassName?: string;
    tableClassName?: string;
    tableStyle?: React.CSSProperties;
    toolbar?: DataTableToolbar;
    sortable?: DataTableSortable;
    pagination?: DataTablePagination;
    emptyState?: React.ReactNode;
    rowClassName?: string | ((row: T, index: number) => string | undefined);
    rowStyle?: React.CSSProperties | ((row: T, index: number) => React.CSSProperties | undefined);
    responsiveMode?: 'table' | 'stacked-cards';
}

const DataTable = <T,>({
    columns,
    rows,
    rowKey,
    tableId,
    wrapperClassName = 'table-responsive',
    tableClassName = 'table',
    tableStyle,
    toolbar,
    sortable,
    pagination,
    emptyState,
    rowClassName,
    rowStyle,
    responsiveMode = 'table',
}: DataTableProps<T>) => {
    const clampPage = (page: number, maxPages: number) => {
        if (!Number.isFinite(page) || !Number.isFinite(maxPages)) {
            return 1;
        }

        const normalizedMaxPages = Math.max(1, Math.floor(maxPages));
        const normalizedPage = Math.floor(page);
        return Math.min(normalizedMaxPages, Math.max(1, normalizedPage));
    };

    const normalizedPagination = pagination ? { ...pagination } : null;
    const normalizedItemsPerPage = normalizedPagination?.itemsPerPage && normalizedPagination.itemsPerPage > 0
        ? Math.floor(normalizedPagination.itemsPerPage)
        : null;
    const normalizedTotalItems = normalizedPagination
        ? Math.max(0, Math.floor(normalizedPagination.totalItems ?? rows.length))
        : rows.length;
    const resolvedTotalPages = normalizedPagination
        ? (
            normalizedItemsPerPage
                ? Math.max(1, Math.ceil(normalizedTotalItems / normalizedItemsPerPage))
                : Math.max(1, Math.floor(normalizedPagination.totalPages ?? 1))
        )
        : 1;

    const currentPage = normalizedPagination
        ? clampPage(normalizedPagination.currentPage, resolvedTotalPages)
        : 1;

    const handlePageChange = (nextPage: number) => {
        if (!normalizedPagination) {
            return;
        }

        normalizedPagination.onPageChange(clampPage(nextPage, resolvedTotalPages));
    };

    const getSortPrimitive = (value: string | number | null | undefined): string | number => {
        if (typeof value === 'number') {
            return value;
        }

        return (value ?? '').toString().toLowerCase();
    };

    const sortedRows = (() => {
        if (!sortable?.clientSide) {
            return rows;
        }

        const activeColumn = columns.find((column) => (column.sortKey || column.id) === sortable.field);
        if (!activeColumn?.sortValue) {
            return rows;
        }

        return [...rows].sort((a, b) => {
            const aValue = getSortPrimitive(activeColumn.sortValue?.(a));
            const bValue = getSortPrimitive(activeColumn.sortValue?.(b));

            if (aValue < bValue) {
                return sortable.direction === 'asc' ? -1 : 1;
            }

            if (aValue > bValue) {
                return sortable.direction === 'asc' ? 1 : -1;
            }

            return 0;
        });
    })();

    const startItem = normalizedItemsPerPage ? ((currentPage - 1) * normalizedItemsPerPage) + 1 : 1;
    const endItem = normalizedItemsPerPage
        ? Math.min(currentPage * normalizedItemsPerPage, normalizedTotalItems)
        : sortedRows.length;
    const pagedRows = normalizedItemsPerPage
        ? sortedRows.slice(startItem - 1, endItem)
        : sortedRows;
    const paginationInfo = normalizedPagination?.info ?? (
        normalizedItemsPerPage
            ? `Showing ${normalizedTotalItems > 0 ? startItem : 0} to ${normalizedTotalItems > 0 ? endItem : 0} of ${normalizedTotalItems} ${normalizedPagination?.itemsLabel || 'entries'}`
            : undefined
    );

    const defaultSortIcon = (field: string, isActive: boolean, direction: TableSortDirection) => {
        if (!isActive) {
            return <i className="fas fa-sort ms-1 text-muted"></i>;
        }

        return direction === 'asc'
            ? <i className="fas fa-sort-up ms-1"></i>
            : <i className="fas fa-sort-down ms-1"></i>;
    };

    const entriesOptions = toolbar?.entriesOptions && toolbar.entriesOptions.length > 0
        ? toolbar.entriesOptions
        : [
            { value: 10 },
            { value: 25 },
            { value: 50 },
            { value: 100 },
        ];

    const hasToolbar = Boolean(
        toolbar && (
            (typeof toolbar.searchValue === 'string' && toolbar.onSearchChange) ||
            (typeof toolbar.entriesPerPage === 'number' && toolbar.onEntriesPerPageChange)
        )
    );
    const hasSearchSummary = Boolean(
        toolbar &&
        typeof toolbar.searchValue === 'string' &&
        toolbar.searchValue.trim() !== '' &&
        typeof toolbar.filteredCount === 'number' &&
        typeof toolbar.totalCount === 'number'
    );

    const getPaginationButtonStyle = (isActive = false, isDisabled = false): React.CSSProperties => ({
        padding: '6px 12px',
        border: `1px solid ${isActive ? 'var(--weka-button-bg)' : 'var(--weka-subtle-border-color)'}`,
        borderRadius: '4px',
        backgroundColor: isActive ? 'var(--weka-button-bg)' : 'var(--weka-card-bg)',
        color: isActive ? 'var(--weka-button-text)' : 'var(--weka-text-color)',
        cursor: isDisabled ? 'not-allowed' : (isActive ? 'default' : 'pointer'),
        fontSize: '13px',
        opacity: isDisabled ? 0.5 : 1,
        minWidth: '40px',
    });

    const resolveMobileDisplay = (column: DataTableColumn<T>, columnIndex: number): DataTableMobileDisplay => {
        if (column.mobileDisplay) {
            return column.mobileDisplay;
        }

        if (column.id === 'actions') {
            return 'actions';
        }

        return columnIndex === 0 ? 'title' : 'field';
    };

    const resolveMobileLabel = (column: DataTableColumn<T>): React.ReactNode => {
        if (column.mobileLabel !== undefined) {
            return column.mobileLabel;
        }

        if (typeof column.header === 'string' || typeof column.header === 'number') {
            return column.header;
        }

        return null;
    };

    const titleColumn = columns.find((column, columnIndex) => resolveMobileDisplay(column, columnIndex) === 'title') ?? columns[0];
    const fieldColumns = columns.filter((column, columnIndex) => resolveMobileDisplay(column, columnIndex) === 'field');
    const actionColumns = columns.filter((column, columnIndex) => resolveMobileDisplay(column, columnIndex) === 'actions');
    const hasResponsiveCards = responsiveMode === 'stacked-cards';
    const desktopWrapperClassName = [
        'weka-data-table__desktop',
        hasResponsiveCards ? 'weka-data-table__desktop--stacked' : '',
        wrapperClassName,
    ].filter(Boolean).join(' ');
    const mobileCardsClassName = [
        'weka-data-table__mobile-cards',
        hasResponsiveCards ? 'weka-data-table__mobile-cards--active' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={`weka-data-table${hasResponsiveCards ? ' weka-data-table--stacked' : ''}`}>
            {hasToolbar && (
                <div className={toolbar?.className || 'row mb-3'}>
                    <div className="col-12">
                        <div className="row">
                            <div className="col-md-6">
                                {typeof toolbar?.searchValue === 'string' && toolbar.onSearchChange && (
                                    <div className={toolbar.searchClassName || 'input-group'}>
                                        {toolbar.showSearchIcon !== false && (
                                            <span className="input-group-text">
                                                <i className="fas fa-search"></i>
                                            </span>
                                        )}
                                        {toolbar.searchLabel && <label className="me-2">{toolbar.searchLabel}</label>}
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={toolbar.searchPlaceholder || 'Search...'}
                                            value={toolbar.searchValue}
                                            onChange={(e) => toolbar.onSearchChange?.(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6 d-flex justify-content-end align-items-center">
                                {typeof toolbar?.entriesPerPage === 'number' && toolbar.onEntriesPerPageChange && (
                                    <>
                                        <label className="me-2">{toolbar.showLabel || 'Show:'}</label>
                                        <select
                                            className="form-select w-auto"
                                            value={toolbar.entriesPerPage}
                                            onChange={(e) => toolbar.onEntriesPerPageChange?.(Number(e.target.value))}
                                        >
                                            {entriesOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label || option.value}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="ms-2 text-muted">{toolbar.entriesLabel || 'entries'}</span>
                                    </>
                                )}
                            </div>
                        </div>
                        {hasSearchSummary && (
                            <small className="text-muted d-block mt-2">
                                Showing {toolbar?.filteredCount} of {toolbar?.totalCount} {toolbar?.countLabel || 'results'}
                                {toolbar?.onClearSearch && (
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 ms-2"
                                        onClick={toolbar.onClearSearch}
                                    >
                                        {toolbar.clearSearchLabel || 'Clear search'}
                                    </button>
                                )}
                            </small>
                        )}
                    </div>
                </div>
            )}
            <div className={desktopWrapperClassName}>
                <table id={tableId} className={tableClassName} style={tableStyle}>
                    <thead>
                        <tr>
                            {columns.map((column) => {
                                const sortField = column.sortKey || column.id;
                                const isSortable = Boolean(sortable && column.sortable);
                                const isActiveSort = Boolean(sortable && sortable.field === sortField);
                                const sortClass = isSortable
                                    ? `sorting ${isActiveSort ? (sortable!.direction === 'asc' ? 'sorting_asc' : 'sorting_desc') : ''}`
                                    : '';
                                const className = [column.headerClassName, sortClass].filter(Boolean).join(' ');

                                return (
                                    <th
                                        key={column.id}
                                        className={className || undefined}
                                        style={{
                                            ...column.headerStyle,
                                            ...(isSortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
                                        }}
                                        onClick={isSortable ? () => sortable!.onSort(sortField) : undefined}
                                    >
                                        {column.header}
                                        {isSortable && (
                                            sortable?.getSortIcon
                                                ? sortable.getSortIcon(sortField, isActiveSort, sortable.direction)
                                                : defaultSortIcon(sortField, isActiveSort, sortable!.direction)
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {pagedRows.length > 0 ? pagedRows.map((row, rowIndex) => {
                            const resolvedRowClassName =
                                typeof rowClassName === 'function' ? rowClassName(row, rowIndex) : rowClassName;
                            const resolvedRowStyle =
                                typeof rowStyle === 'function' ? rowStyle(row, rowIndex) : rowStyle;

                            return (
                                <tr key={rowKey(row, rowIndex)} className={resolvedRowClassName} style={resolvedRowStyle}>
                                    {columns.map((column) => {
                                        const resolvedCellClassName =
                                            typeof column.cellClassName === 'function'
                                                ? column.cellClassName(row, rowIndex)
                                                : column.cellClassName;
                                        const resolvedCellStyle =
                                            typeof column.cellStyle === 'function'
                                                ? column.cellStyle(row, rowIndex)
                                                : column.cellStyle;

                                        return (
                                            <td key={column.id} className={resolvedCellClassName} style={resolvedCellStyle}>
                                                {column.render(row, rowIndex)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={Math.max(columns.length, 1)}>
                                    {emptyState || <div className="text-muted py-3">No records found.</div>}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {hasResponsiveCards && (
                <div className={mobileCardsClassName}>
                    {pagedRows.length > 0 ? pagedRows.map((row, rowIndex) => {
                        const resolvedRowClassName =
                            typeof rowClassName === 'function' ? rowClassName(row, rowIndex) : rowClassName;
                        const resolvedRowStyle =
                            typeof rowStyle === 'function' ? rowStyle(row, rowIndex) : rowStyle;

                        return (
                            <article
                                key={rowKey(row, rowIndex)}
                                className={['weka-data-table__mobile-card', resolvedRowClassName].filter(Boolean).join(' ')}
                                style={resolvedRowStyle}
                            >
                                {titleColumn && (
                                    <div className="weka-data-table__mobile-card-title">
                                        {titleColumn.render(row, rowIndex)}
                                    </div>
                                )}
                                {fieldColumns.length > 0 && (
                                    <dl className="weka-data-table__mobile-fields">
                                        {fieldColumns.map((column) => {
                                            const label = resolveMobileLabel(column);
                                            if (label === null || label === '') {
                                                return null;
                                            }

                                            return (
                                                <div key={column.id} className="weka-data-table__mobile-field">
                                                    <dt className="weka-data-table__mobile-label">{label}</dt>
                                                    <dd className="weka-data-table__mobile-value">{column.render(row, rowIndex)}</dd>
                                                </div>
                                            );
                                        })}
                                    </dl>
                                )}
                                {actionColumns.length > 0 && (
                                    <div className="weka-data-table__mobile-actions">
                                        {actionColumns.map((column) => (
                                            <div key={column.id} className="weka-data-table__mobile-actions-slot">
                                                {column.render(row, rowIndex)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </article>
                        );
                    }) : (
                        <div className="weka-data-table__mobile-empty">
                            {emptyState || <div className="text-muted py-3">No records found.</div>}
                        </div>
                    )}
                </div>
            )}
            {normalizedPagination && (
                normalizedPagination.mode === 'simple' ? (
                    <div className={normalizedPagination.className} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', fontSize: '14px', color: '#6c757d' }}>
                        <div>{paginationInfo}</div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid var(--weka-subtle-border-color)',
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--weka-card-bg)',
                                    color: 'var(--weka-text-color)',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                }}
                            >
                                {normalizedPagination.previousLabel || 'Previous'}
                            </button>
                            <button
                                type="button"
                                disabled
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid var(--weka-button-bg)',
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--weka-button-bg)',
                                    color: 'var(--weka-button-text)',
                                    cursor: 'default',
                                    fontSize: '13px',
                                }}
                            >
                                {currentPage}
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === resolvedTotalPages}
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid var(--weka-subtle-border-color)',
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--weka-card-bg)',
                                    color: 'var(--weka-text-color)',
                                    cursor: currentPage === resolvedTotalPages ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    opacity: currentPage === resolvedTotalPages ? 0.5 : 1,
                                }}
                            >
                                {normalizedPagination.nextLabel || 'Next'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <nav aria-label="Table pagination" className={normalizedPagination.className || 'mt-3'}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <div className="text-muted">{paginationInfo}</div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    style={getPaginationButtonStyle(false, currentPage === 1)}
                                >
                                    {normalizedPagination.previousLabel || 'Previous'}
                                </button>
                                {Array.from({ length: resolvedTotalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        type="button"
                                        onClick={() => handlePageChange(page)}
                                        disabled={currentPage === page}
                                        style={getPaginationButtonStyle(currentPage === page, currentPage === page)}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === resolvedTotalPages}
                                    style={getPaginationButtonStyle(false, currentPage === resolvedTotalPages)}
                                >
                                    {normalizedPagination.nextLabel || 'Next'}
                                </button>
                            </div>
                        </div>
                    </nav>
                )
            )}
        </div>
    );
};

export default DataTable;
